import type { Company, CompanyStatus } from './types.ts';
import { COMPANY_STATUSES } from './companyStatus.ts';

/** Value of the status filter — a concrete status, or `'all'` for no filter. */
export type StatusFilter = CompanyStatus | 'all';

/**
 * Sentinel filter value matching companies whose field is empty/null. A
 * double-underscore token can't collide with real human-entered free text.
 */
export const FIELD_NOT_SET = '__none__';

/** The company-info fields the list can filter on. contractor_types is a
 *  string[] field; the others are scalar free-text. */
export type FilterableField = 'contractor_types' | 'cluster_region' | 'station';

export type CompanyFilters = {
  /** Free-text search matched against company/contact/email, vendor number,
   *  contract number and the company-filled primary contact name/email; '' = no search. */
  query: string;
  /** 'all' = every active status (deactivated hidden); 'any' = no status
   *  condition at all — used by the faceted pill counts, which must see
   *  deactivated rows to count them under their own pill. */
  status: StatusFilter | 'any';
  /** Selected contractor types — OR within the field. `[]` = no filter; a
   *  FIELD_NOT_SET entry matches companies whose field is empty/whitespace;
   *  other entries are exact (trimmed) matches. */
  contractorType: string[];
  /** Same contract as contractorType. */
  clusterRegion: string[];
  /** Same contract as contractorType. */
  station: string[];
};

// [] → no filter (field condition disabled). Otherwise the company matches when
// its (trimmed) field value is in the selected set — OR within the field — where
// a FIELD_NOT_SET entry matches an empty/whitespace value. Values are stored
// trimmed by the create modal, but trim here too in case other write paths ever
// differ.
function matchesField(value: string | null, filters: string[]): boolean {
  if (filters.length === 0) return true;
  const v = (value ?? '').trim();
  if (v === '') return filters.includes(FIELD_NOT_SET);
  return filters.includes(v);
}

// Array-field variant of matchesField: the vendor matches when ANY of its
// (trimmed, non-empty) values is in the selected set; FIELD_NOT_SET matches a
// NULL/empty/whitespace-only list.
function matchesListField(values: string[] | null, filters: string[]): boolean {
  if (filters.length === 0) return true;
  const list = (values ?? []).map((v) => v.trim()).filter((v) => v !== '');
  if (list.length === 0) return filters.includes(FIELD_NOT_SET);
  return list.some((v) => filters.includes(v));
}

/**
 * Filter the admin company list. All conditions AND together: a free-text query
 * (matched against company name, contact name and email, vendor number, contract
 * number and the company-filled primary contact name/email), a status, and
 * set-membership matches on the contractor type / cluster region / station
 * fields (OR within each field). An empty array disables that field's condition.
 * For status: `'all'` keeps every *active* status (deactivated rows hidden),
 * `'any'` applies no status condition at all (deactivated rows included — used by
 * the faceted pill counts), and a concrete status keeps only that status.
 */
export function filterCompanies(companies: Company[], filters: CompanyFilters): Company[] {
  const q = filters.query.trim().toLowerCase();
  return companies.filter((c) => {
    if (filters.status === 'any') {
      // no status condition — faceted counts need deactivated rows included
    } else if (filters.status === 'all') {
      // 'all' means every *active* status — deactivated companies appear only
      // when their pill is selected explicitly.
      if (c.status === 'deactivated') return false;
    } else if (c.status !== filters.status) {
      return false;
    }
    if (!matchesListField(c.contractor_types, filters.contractorType)) return false;
    if (!matchesField(c.cluster_region, filters.clusterRegion)) return false;
    if (!matchesField(c.station, filters.station)) return false;
    if (!q) return true;
    // Nullable company-filled / admin fields default to '' so a missing value
    // never matches (and never throws).
    return (
      c.company_name.toLowerCase().includes(q) ||
      c.contact_name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.vendor_number ?? '').toLowerCase().includes(q) ||
      (c.contract_numbers ?? []).some((n) => n.toLowerCase().includes(q)) ||
      (c.primary_contact_name ?? '').toLowerCase().includes(q) ||
      (c.contact_email ?? '').toLowerCase().includes(q)
    );
  });
}

export type FieldValues = {
  /** Distinct non-empty trimmed values, deduped and localeCompare-sorted. */
  values: string[];
  /** True when at least one company has the field empty — drives "(Not set)". */
  hasNotSet: boolean;
};

/**
 * Distinct values of a free-text company-info field across the *unfiltered*
 * list, for the filter dropdowns (options stay stable while other filters
 * narrow the view). Dedupe is exact/case-sensitive: contractor types come from
 * a curated dropdown, and merging "North"/"north" heuristically would be
 * unpredictable.
 */
// Trimmed, non-empty entries of a filterable field — scalar fields contribute
// at most one entry, contractor_types contributes one per type.
function fieldEntries(c: Company, field: FilterableField): string[] {
  const raw = c[field];
  const list = Array.isArray(raw) ? raw : [raw ?? ''];
  return list.map((v) => v.trim()).filter((v) => v !== '');
}

export function distinctFieldValues(companies: Company[], field: FilterableField): FieldValues {
  const set = new Set<string>();
  let hasNotSet = false;
  for (const c of companies) {
    const entries = fieldEntries(c, field);
    if (entries.length === 0) hasNotSet = true;
    for (const v of entries) set.add(v);
  }
  return { values: [...set].sort((a, b) => a.localeCompare(b)), hasNotSet };
}

/**
 * FieldValues for a field whose dropdown options are curated in Settings
 * (branding) rather than derived from the data. Options keep their configured
 * order (matching the Settings editor and the create-company modal), trimmed,
 * with empties and duplicates dropped. (Settings already persists the list
 * through `normalizeStringList`, which trims/dedupes — the re-normalisation
 * here is belt-and-braces for any other write path.) `hasNotSet` reports whether
 * any company currently misses the field (informational; the "(Not set)" filter
 * is offered for these fields regardless, see parseFieldFilterParam).
 */
export function configuredFieldValues(
  configured: string[],
  companies: Company[],
  field: FilterableField,
): FieldValues {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const raw of configured) {
    const v = raw.trim();
    if (v !== '' && !seen.has(v)) {
      seen.add(v);
      values.push(v);
    }
  }
  return { values, hasNotSet: companies.some((c) => fieldEntries(c, field).length === 0) };
}

export type StatusCounts = {
  /** Count per status — every status in COMPANY_STATUSES is present (0 when none). */
  counts: Record<CompanyStatus, number>;
  /** Number of *active* companies — deactivated rows count only under their own pill. */
  total: number;
};

// Maps a raw ?status= query value to a StatusFilter, defaulting unknown/empty
// values to 'all' so a hand-edited URL can never crash the filter.
export function parseStatusFilterParam(raw: string | null): StatusFilter {
  if (raw && (COMPANY_STATUSES as readonly string[]).includes(raw)) {
    return raw as StatusFilter;
  }
  return 'all';
}

/**
 * Maps the repeatable raw ?type= / ?region= / ?station= query values to a
 * validated, deduped field-filter array. FIELD_NOT_SET always passes through —
 * it's an admin-meaningful "find vendors missing this field" filter, offered
 * regardless of whether any vendor currently has it empty (it simply returns no
 * rows when none do). A concrete value passes only while it exists in the data;
 * anything else (hand-edited URL, stale link after the last matching company was
 * deleted) is dropped so the control can never silently disagree with the table.
 * Order follows `allowed.values` (with FIELD_NOT_SET last) for a deterministic URL.
 */
export function parseFieldFilterParam(raw: string[], allowed: FieldValues): string[] {
  const wanted = new Set(raw);
  const out = allowed.values.filter((v) => wanted.has(v));
  if (wanted.has(FIELD_NOT_SET)) out.push(FIELD_NOT_SET);
  return out;
}

/**
 * Free-text match for the admin Dashboard search: company name, contact name,
 * and email (case-insensitive, trimmed). An empty query matches everything.
 */
export function matchesCompanyQuery(
  c: Pick<Company, 'company_name' | 'contact_name' | 'email'>,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    c.company_name.toLowerCase().includes(q) ||
    c.contact_name.toLowerCase().includes(q) ||
    c.email.toLowerCase().includes(q)
  );
}

/**
 * Lightweight filter spec for the Dashboard and Expiring Documents views.
 * Each dimension is an array of selected values; an empty array means "no
 * constraint on that dimension". All active dimensions AND together.
 */
export type CompanyFilter = {
  contractorTypes: string[];
  clusterRegions: string[];
  stations: string[];
};

type CompanyFilterable = {
  contractor_types?: string[] | null;
  cluster_region?: string | null;
  station?: string | null;
};

/**
 * Returns true when company `c` satisfies every active dimension of filter `f`.
 * contractor_types is an array field — the company matches when its types and the
 * selected types intersect. cluster_region and station are scalar — the company
 * matches when its value is in the selected set. An empty selection disables that
 * dimension (matches everything).
 */
export function matchesCompanyFilter(c: CompanyFilterable, f: CompanyFilter): boolean {
  if (
    f.contractorTypes.length > 0 &&
    !(c.contractor_types ?? []).some((t) => f.contractorTypes.includes(t))
  )
    return false;
  if (f.clusterRegions.length > 0 && !(c.cluster_region && f.clusterRegions.includes(c.cluster_region)))
    return false;
  if (f.stations.length > 0 && !(c.station && f.stations.includes(c.station))) return false;
  return true;
}

/**
 * Tally companies by status for the filter strip's count badges. Every status is
 * seeded to 0 first, so the strip renders a stable, complete set of pills without
 * each one having to guard for a missing key.
 */
export function countByStatus(companies: Company[]): StatusCounts {
  const counts = Object.fromEntries(COMPANY_STATUSES.map((s) => [s, 0])) as Record<
    CompanyStatus,
    number
  >;
  let total = 0;
  for (const c of companies) {
    if (c.status in counts) counts[c.status] += 1;
    if (c.status !== 'deactivated') total += 1;
  }
  return { counts, total };
}
